import { CourseCard } from "./cards/CourseCard";
import { EventCard } from "./cards/EventCard";
import { VideoCard } from "./cards/VideoCard";
import { PodcastCard } from "./cards/PodcastCard";
import { type Dict, type Lang } from "@/lib/i18n";
import type { Module } from "@/lib/lecturehub/types";

export { moduleHref } from "./moduleHref";

// Shared server/client dispatcher — sync by design; the caller supplies the
// resolved Dict (server: await getDict(lang); client: useDict()).
type CardProps = {
  lang: Lang;
  t: Dict;
  module: Module;
  enrolled?: boolean;
  /** Cloudflare Images account hash, threaded down so posters render CF
   *  delivery URLs. null/omitted → posters fall back to Image.uri. */
  cfHash?: string | null;
  /** Link to the in-app /library detail instead of the public one. */
  library?: boolean;
};

export function ModuleCard({
  lang,
  t,
  module: m,
  enrolled = false,
  cfHash = null,
  library = false,
}: CardProps) {
  switch (m.refType) {
    case "ott":
      return (
        <VideoCard
          lang={lang}
          t={t}
          module={m}
          enrolled={enrolled}
          cfHash={cfHash}
          library={library}
        />
      );
    case "podcast":
      return (
        <PodcastCard
          lang={lang}
          t={t}
          module={m}
          enrolled={enrolled}
          cfHash={cfHash}
          library={library}
        />
      );
    case "event":
      return (
        <EventCard
          lang={lang}
          t={t}
          module={m}
          enrolled={enrolled}
          cfHash={cfHash}
          library={library}
        />
      );
    case "course":
    default:
      return (
        <CourseCard
          lang={lang}
          t={t}
          module={m}
          enrolled={enrolled}
          cfHash={cfHash}
          library={library}
        />
      );
  }
}

export function ModuleGrid({
  modules,
  lang,
  t,
  emptyState,
  enrolledIds,
  cols = 3,
  cfHash = null,
  library = false,
}: {
  modules: Module[];
  lang: Lang;
  t: Dict;
  emptyState?: React.ReactNode;
  enrolledIds?: Set<string>;
  cols?: 3 | 4;
  cfHash?: string | null;
  library?: boolean;
}) {
  if (modules.length === 0) {
    // A caller's element (the shared EmptyState) renders as is — it used to be
    // wrapped in this muted box too, a card inside a box on /saved. The box is
    // only for the bare fallback sentence.
    return (
      emptyState ?? (
        <div className="bg-muted/40 p-10 text-center text-muted-foreground rounded-2xl">
          {t.listing.nothingToShow}
        </div>
      )
    );
  }
  const gridCls =
    cols === 4
      ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-10 md:gap-x-8 md:gap-y-12"
      : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10 md:gap-x-10 md:gap-y-12";
  return (
    <div className={gridCls}>
      {modules.map((m) => (
        <ModuleCard
          key={m.id}
          module={m}
          lang={lang}
          t={t}
          enrolled={enrolledIds?.has(String(m.id)) ?? false}
          cfHash={cfHash}
          library={library}
        />
      ))}
    </div>
  );
}
